import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO } from '../../utils/seo';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '算命学とは',
    description: '算命学は、古代中国に起源を持ち、人間もこの地球上に生を受けた自然の産物であり、自然の一部分であるという自然思想に基づいた学問です。',
    author: {
      '@type': 'Person',
      name: '橘穂',
    },
    publisher: {
      '@type': 'Organization',
      name: '橘穂の算命学カウンセリング',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/about`,
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToHomeSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    navigate('/#' + sectionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F1E8] to-white overflow-x-hidden">
      <SEO
        title="算命学とは｜橘穂の算命学カウンセリング"
        description="算命学は、古代中国に起源を持ち、人間もこの地球上に生を受けた自然の産物であり、自然の一部分であるという自然思想に基づいた学問です。生年月日から導き出される命式をもとに、人生の流れを読み解いていきます。"
        keywords="算命学,自然思想,命式,宿命,才能,人生の流れ,運命鑑定"
        canonical={`${siteUrl}/about`}
        ogTitle="算命学とは｜橘穂の算命学カウンセリング"
        ogDescription="算命学は、古代中国に起源を持つ自然思想に基づいた学問です。生年月日から導き出される命式をもとに、人生の流れを読み解きます。"
        ogType="article"
        schema={schema}
      />

      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-[#F5F1E8]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer min-w-0"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/dab67413-5a70-4602-b752-4997924ca2b6.png"
                alt="橘穂の算命学カウンセリング ロゴ"
                className="w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0"
              />
              <span className="text-sm sm:text-lg font-serif text-[#8B4513] whitespace-nowrap truncate">
                橘穂の算命学カウンセリング
              </span>
            </Link>
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10">
              <Link
                to="/"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F]"
              >
                ホーム
              </Link>
              <Link
                to="/about"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#6B8E7F]"
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F]"
              >
                プロフィール
              </Link>
              <button
                onClick={() => goToHomeSection('menu')}
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F] cursor-pointer"
              >
                メニュー
              </button>
              <button
                onClick={() => goToHomeSection('contact')}
                className="text-sm font-light tracking-wide transition-colors whitespace-nowrap text-[#2C2C2C] hover:text-[#6B8E7F] cursor-pointer"
              >
                ご予約
              </button>
            </div>
            <div className="hidden lg:block">
              <button
                onClick={() => goToHomeSection('campaign')}
                className="px-5 py-2 rounded-full border border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="text-sm mr-1">初回</span>
                <span className="text-sm">特別鑑定</span>
              </button>
            </div>
            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2C2C2C] cursor-pointer"
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg border-t border-[#E0E0E0]">
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                ホーム
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#6B8E7F] hover:bg-[#F5F1E8] rounded-lg"
              >
                算命学とは
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg"
              >
                プロフィール
              </Link>
              <button
                onClick={() => goToHomeSection('menu')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                メニュー
              </button>
              <button
                onClick={() => goToHomeSection('contact')}
                className="block w-full text-left px-4 py-3 text-sm font-light text-[#2C2C2C] hover:bg-[#F5F1E8] rounded-lg cursor-pointer"
              >
                ご予約
              </button>
              <div className="pt-2 px-4">
                <button
                  onClick={() => goToHomeSection('campaign')}
                  className="w-full py-3 rounded-full border border-[#2C2C2C] text-[#2C2C2C] text-sm font-light hover:bg-[#2C2C2C] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  初回特別鑑定
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2C2C2C] mb-4 sm:mb-6 leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            算命学とは
          </h1>
          <p className="text-[#8B7355] text-xs sm:text-sm font-light">
            自然思想に基づく学問
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 sm:py-20 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 sm:p-10 lg:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">
              <h2
                className="text-2xl sm:text-3xl font-light text-[#2C2C2C] mb-6 sm:mb-8"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                算命学とは
              </h2>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                算命学は、古代中国に起源を持ち、人間もこの地球上に生を受けた自然の産物であり、自然の一部分であるという自然思想に基づいた学問です。
              </p>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-6 sm:mb-8">
                生年月日から導き出される命式をもとに、<br />
                その人が持って生まれた才能や思考の傾向、行動パターン、巡り来る時の流れと宿命を関連させながら自然の移ろいに当てはめ、人生の流れを読み解いていきます。
              </p>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose mb-8 sm:mb-12">
                算命学は未来を当てるためだけの占いではありません。<br />
                宿命を知ることで、長い人生航路を安全に進むための『航路図』とも言えるでしょう。
              </p>

              <div className="bg-[#F5F1E8] rounded-xl p-5 sm:p-8 mb-6 sm:mb-8">
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      人生に迷いがある
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      人生の転機を知りたい
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      恋人、家族や対人関係で悩みを抱えている
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E7F] mt-1.5 sm:mt-2 flex-shrink-0"></span>
                    <span className="text-[#4A4A4A] text-sm sm:text-base font-light">
                      子育ての悩み
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-[#6B8E7F] text-base sm:text-lg font-light leading-loose text-center mb-4">
                『どうして上手くいかないんだろう…』
              </p>

              <p className="text-[#4A4A4A] text-sm sm:text-base font-light leading-loose text-center">
                その答えは、実は<br />
                『あなた自身』の中にあるかもしれません。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2C2C] mb-4 sm:mb-6 leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            あなたの人生に、<br />
            新しい視点を。
          </h2>
          <p className="text-[#8B7355] text-sm sm:text-base font-light mb-8 sm:mb-10">
            初回特別鑑定、受付中です
          </p>
          <button
            onClick={() => goToHomeSection('contact')}
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-[#6B8E7F] text-white rounded-full text-sm font-light tracking-wider hover:bg-[#5A7D6E] transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>鑑定を申し込む</span>
            <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#8B4513] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/dab67413-5a70-4602-b752-4997924ca2b6.png"
                  alt="橘穂の算命学カウンセリング ロゴ"
                  className="w-8 h-8 sm:w-10 sm:h-10"
                />
                <span className="text-sm sm:text-base font-serif whitespace-nowrap">
                  橘穂の算命学カウンセリング
                </span>
              </div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                サービス
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    ホーム
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    算命学とは
                  </Link>
                </li>
                <li>
                  <Link
                    to="/profile"
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap"
                  >
                    プロフィール
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => goToHomeSection('menu')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    鑑定メニュー
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご予約
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    営業時間
                  </span>
                </li>
                <li>
                  <button
                    onClick={() => goToHomeSection('contact')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    予約方法
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                ご利用条件
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    女性限定について
                  </span>
                </li>
                <li>
                  <span className="text-xs sm:text-sm font-light cursor-pointer hover:text-white/80 transition-colors whitespace-nowrap">
                    対面鑑定エリア
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold mb-3 sm:mb-4 pb-2 border-b border-white/20">
                お問い合わせ
              </h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <button
                    onClick={() => goToHomeSection('contact')}
                    className="text-xs sm:text-sm font-light hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    メールフォーム
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/0ef220cc-5e3a-48f0-bc37-23fb3250621c.png"
                alt="橘穂の算命学カウンセリング"
                className="h-7 w-7 sm:h-8 sm:w-8 object-contain"
              />
              <div>
                <div className="text-sm sm:text-lg font-light">
                  橘穂の算命学カウンセリング
                </div>
                <p className="text-[10px] sm:text-xs font-light mt-1">
                  女性限定・完全予約制
                </p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-[10px] sm:text-xs font-light mb-2">
                © 2025 All Rights Reserved
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] sm:text-xs font-light hover:text-white/80 transition-colors whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
